import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { type JSX, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import { useColorBlindModeEnabled } from 'ts/base/hooks/ColorBlindModeHook';
import { IS_SAFARI } from 'ts/commons/BrowserUtils';
import { CodeLine, CodeLineContainer } from 'ts/commons/code/CodeLineContainer';
import { CodeOutlineBar } from 'ts/commons/code/CodeOutlineBar';
import { CoverageMode } from 'ts/commons/code/CoverageModeSelector';
import { UIUtils } from 'ts/commons/UIUtils';
import type { ETestGapStateEntry } from 'typedefs/ETestGapState';
import type { LineBasedMethodInfo } from 'typedefs/LineBasedMethodInfo';
import type {
	CodeContainerImperativeHandle,
	RenderedCodeProviderProps,
	ScrollToLineOptions,
	VisibleCodeRegion
} from './RenderedCode.context';
import { RenderedCodeContextProvider, useRenderedCodeOptions } from './RenderedCode.context';
import style from './RenderedCode.module.less';

export type RenderedCodeProps = RenderedCodeProviderProps;

/** The test gap and inside/start/end of method location of a source line. */
export type LineStatus = {
	isMethodStart: boolean;
	isMethodEnd: boolean;
	isMethodLine: boolean;
	testGapStatus: ETestGapStateEntry | undefined;
};

/** Returns information for test gap status highlighting */
function processMethodInfos(methodInfos?: LineBasedMethodInfo[]): Map<number, LineStatus> {
	const lineStatusMap = new Map<number, LineStatus>();

	methodInfos?.forEach(method => {
		for (let line = method.lineBasedRegion.start; line <= method.lineBasedRegion.end; line++) {
			lineStatusMap.set(line, {
				isMethodStart: line === method.lineBasedRegion.start,
				isMethodEnd: line === method.lineBasedRegion.end,
				isMethodLine: true,
				testGapStatus: method.testGapStatus
			});
		}
	});

	return lineStatusMap;
}

/**
 * Renders a code snippet. This includes features like selecting identifiers, showing findings (if passed to this
 * component), showing issue/spec-item references, and more.
 */
export function RenderedCode(props: RenderedCodeProps): JSX.Element {
	return (
		<RenderedCodeContextProvider {...props}>
			<RenderedCodeContainer />
		</RenderedCodeContextProvider>
	);
}

/** Renders an expanded macro code snippet. This includes features like selecting identifiers and syntax highlighting. */
export function RenderedExpandedMacroCode(props: RenderedCodeProviderProps): JSX.Element {
	return (
		<RenderedCodeContextProvider {...props}>
			<RenderedMacroCodeContainer />
		</RenderedCodeContextProvider>
	);
}

/** Container for the expanded macro code. */
function RenderedMacroCodeContainer(): JSX.Element {
	const { visibleRegion, linesWithTokens } = useRenderedCodeOptions();

	return (
		<span>
			<CodeLine
				key={'line-expanded-' + visibleRegion.startLine}
				segmentsOfLine={linesWithTokens.get(visibleRegion.startLine)!}
				lineNumber={visibleRegion.startLine}
			/>
		</span>
	);
}

/**
 * Keeps the scroll position visually consistent in the finding detail view when the code snippet is expanded to show
 * the full file.
 */
function useKeepScrollPosition(visibleRegion: VisibleCodeRegion, virtualizer: Virtualizer<HTMLDivElement, Element>) {
	const previousFirstItemRef = useRef<number | null>(null);
	useLayoutEffect(() => {
		if (previousFirstItemRef.current) {
			const line = previousFirstItemRef.current;
			if (visibleRegion.startLine <= line && line <= visibleRegion.endLine) {
				virtualizer.scrollToIndex(line - visibleRegion.startLine, { align: 'start' });
				previousFirstItemRef.current = null;
			}
		}

		return () => {
			const firstItem = virtualizer.getVirtualItems()[0];
			if (firstItem) {
				previousFirstItemRef.current = visibleRegion.startLine + firstItem.index;
			}
		};
	}, [virtualizer, visibleRegion.startLine, visibleRegion.endLine]);
}

/** The vertical padding that is applied to the code within the scroll container (in px) */
export const CODE_PADDING = 12;

function RenderedCodeContainer(): JSX.Element {
	const colorBlindModeEnabled = useColorBlindModeEnabled();
	const {
		visibleRegion,
		visibleLineCount,
		totalLineCount,
		linesWithFindings,
		methodInfosByStartLine,
		lineCoverageByLine,
		hideBorder,
		hideOutline,
		initialScrollToLine,
		searchResults,
		highlightedSearchResult,
		onScroll,
		fontSize,
		imperativeHandleRef,
		methodInfos,
		coverageSettings,
		language
	} = useRenderedCodeOptions();

	const lineNumberWidth = String(totalLineCount).length * 0.7;

	const lineStatusMap = useMemo(() => processMethodInfos(methodInfos), [methodInfos]);

	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		if (scrollContainerRef.current) {
			UIUtils.addHomeAndEndScrollSupport(scrollContainerRef.current);
		}
	}, []);

	const virtualizer = useVirtualizer({
		count: visibleLineCount,
		getItemKey: useCallback((index: number) => visibleRegion.startLine + index, [visibleRegion.startLine]),
		getScrollElement: () => scrollContainerRef.current,
		// Preliminary size of each line. The actual size will be computed during rendering
		estimateSize: () => getCodeLineHeight(fontSize),
		// Don't virtualize in the UI tests
		overscan: localStorage.getItem('no-virtualization') ? visibleLineCount : 60
	});
	useScrollImperativeHandle(virtualizer);

	// Re-adjust the necessary size after changing the font size
	useLayoutEffect(() => {
		virtualizer.measure();
	}, [virtualizer, fontSize]);

	useEffect(() => {
		if (initialScrollToLine === undefined) {
			return;
		}
		imperativeHandleRef.current?.scrollTo(initialScrollToLine);
	}, [imperativeHandleRef, initialScrollToLine]);

	let highlightedSearchLine: number | undefined;
	if (highlightedSearchResult !== undefined && highlightedSearchResult !== -1) {
		highlightedSearchLine = searchResults[highlightedSearchResult]?.line;
	}

	useEffect(() => {
		if (highlightedSearchLine === undefined) {
			return;
		}
		virtualizer.scrollToIndex(highlightedSearchLine, { align: 'center' });
	}, [highlightedSearchLine, virtualizer]);

	useKeepScrollPosition(visibleRegion, virtualizer);

	const virtualItems = virtualizer.getVirtualItems();

	return (
		<pre
			className={clsx('flex max-h-full items-stretch overflow-hidden w-full m-0', {
				'border border-gray-200 border-solid box-border': !hideBorder
			})}
			data-testid="code-container"
		>
			{hideOutline ? null : <CodeOutlineBar />}
			<div
				data-testid="code-scroll-container"
				className={clsx('code-scroll-container overflow-auto bg-white flex-1', {
					[style.showTestCoverage!]: coverageSettings.coverageMode === CoverageMode.TEST_COVERAGE,
					[style.colorBlindMode!]: colorBlindModeEnabled
				})}
				ref={scrollContainerRef}
				onWheel={event => event.stopPropagation()}
				onScroll={event => {
					event.stopPropagation();
					if (onScroll) {
						onScroll();
					}
				}}
			>
				<div style={{ height: `${virtualizer.getTotalSize() + CODE_PADDING}px` }}>
					<table
						className={style.codeContainerArea}
						style={{ fontSize: `${fontSize}px` }}
						data-language={language?.name}
					>
						<tbody
							style={{
								// TS-41996 Rounding needed to fix fine white lines between code lines
								transform: `translateY(${Math.round(virtualItems[0]?.start ?? 0)}px)`
							}}
						>
							{virtualItems.map(virtualRow => {
								const lineNumber = visibleRegion.startLine + virtualRow.index;
								const findingsInLine = linesWithFindings.get(lineNumber)!;

								return (
									<CodeLineContainer
										key={'line-' + lineNumber}
										lineNumber={lineNumber}
										lineNumberWidthInEm={lineNumberWidth}
										findingsInLine={findingsInLine}
										methodStartingHere={methodInfosByStartLine.get(lineNumber)}
										lineCoverageState={lineCoverageByLine.get(lineNumber)}
										lineStatus={lineStatusMap.get(lineNumber)}
									/>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		</pre>
	);
}

function useScrollImperativeHandle(virtualizer: Virtualizer<HTMLDivElement, Element>) {
	const { visibleRegion, fontSize, imperativeHandleRef, highlightedLinesStore } = useRenderedCodeOptions();
	useImperativeHandle(imperativeHandleRef, () => {
		const scrollElement = virtualizer.scrollElement;
		return {
			getCenterLine: () => {
				if (scrollElement) {
					// First find line number of line shown at the middle
					// Calculate the middle line using the half box height, the current and maximal scroll position and map this to the number of lines
					return (
						(scrollElement.scrollTop - CODE_PADDING + scrollElement.clientHeight / 2) /
							getCodeLineHeight(fontSize) +
						visibleRegion.startLine -
						1
					);
				}
				return 1;
			},
			getScrollOffsetX: () => scrollElement?.scrollLeft ?? 0,
			getScrollOffsetY: () => scrollElement?.scrollTop ?? 0,
			scrollToOffsetX: (offsetX: number) => {
				if (scrollElement) {
					scrollElement.scrollLeft = offsetX;
				}
			},
			scrollToOffsetY: (offsetY: number) => {
				virtualizer.scrollToOffset(offsetY, { align: 'center' });
			},
			scrollTo: ({ line, align = 'center', flash = false, scrollIntoViewport = false }: ScrollToLineOptions) => {
				let lineToQuery: number;
				if ((visibleRegion.endLine - visibleRegion.startLine) / 2 < line - visibleRegion.startLine) {
					lineToQuery = Math.floor(line);
				} else {
					lineToQuery = Math.ceil(line);
				}
				const index = lineToQuery - visibleRegion.startLine;
				const offset = virtualizer.getOffsetForIndex(index, align)!;
				const offsetY = Math.max(offset[0] + getCodeLineHeight(fontSize) * (line - lineToQuery - 0.5), 0);
				requestAnimationFrame(() => {
					virtualizer.scrollToOffset(offsetY, { align: 'start' });
					if (scrollIntoViewport) {
						// If the container is not scrollable, scroll the line element into view instead.
						if (scrollElement && !(scrollElement.scrollHeight > scrollElement.clientHeight)) {
							const lineElement = scrollElement.querySelector(`tr[data-line="${line}"]`);
							if (lineElement) {
								lineElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
							}
						} else {
							scrollElement?.scrollIntoView({ block: 'start', behavior: 'smooth' });
						}
					}
				});
				if (flash) {
					highlightedLinesStore.setState({ flashHighlightedLine: lineToQuery });
				}
				return offsetY;
			},
			getVisibleRegion() {
				return visibleRegion;
			}
		} satisfies CodeContainerImperativeHandle;
	}, [virtualizer, fontSize, visibleRegion, highlightedLinesStore]);
}

/** Returns the height of a code line in pixels. */
export function getCodeLineHeight(codeFontSize: number) {
	const size = codeFontSize * 1.25;
	if (IS_SAFARI) {
		// Safari renders the elements with the height rounded down
		return Math.floor(size);
	}
	return size;
}

/** Returns the vertical offset that a line has within the code scroll container. */
export function getLineOffset(
	fontSize: number,
	lineNumber: number,
	offsetY: number,
	visibleRegion: VisibleCodeRegion | undefined
) {
	return offsetY + getCodeLineHeight(fontSize) * (lineNumber - (visibleRegion?.startLine ?? 1));
}
